import { Box, Stack, Typography, useTheme, VirtualIcon, Tile, Button } from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { FormattedMessage, IntlShape, useIntl } from "react-intl"
import { useTelemedicineForPediatricsActivationStatus } from "#/components/telemedicine/pediatrics/hooks/useTelemedicineForPediatricsActivation"
import { AccessBlueberryAccountDialog } from "#/components/telemedicine/pediatrics/AccessBlueberryAccountDialog"
import { useButtonClickTracking } from "#/services/analytics/useButtonClickTracking"

function getTelemedicineSectionContent(intl: IntlShape, isActivated: boolean) {
  if (isActivated) {
    return {
      heading: intl.formatMessage({ defaultMessage: "Your Blueberry account has been activated" }),
      bodyContent: intl.formatMessage({
        defaultMessage: "Get diagnoses, care, and school notes in minutes through the Blueberry mobile app."
      })
    }
  }
  return {
    heading: null,
    bodyContent: intl.formatMessage({
      defaultMessage:
        "Your Carrot benefit includes free access to Blueberry Pediatrics, offering unlimited, on-demand 24/7 virtual care from board-certified pediatricians through video or messaging in one easy-to-use app."
    })
  }
}

export function TelemedicineSection(): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { activationStatus, ctaButtonText, isLoading } = useTelemedicineForPediatricsActivationStatus()
  const { trackButtonClick } = useButtonClickTracking()
  if (isLoading) {
    return null
  }
  const isActivated = activationStatus === "activated"
  const content = getTelemedicineSectionContent(intl, isActivated)

  const handleButtonClick = () => {
    trackButtonClick({
      name: ctaButtonText
    })
    setDialogOpen(true)
  }

  return (
    <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.sm)} alignItems="center">
        <VirtualIcon />
        <Typography variant="h3">
          <FormattedMessage defaultMessage="Via telemedicine" />
        </Typography>
      </Stack>
      <Typography>
        <FormattedMessage defaultMessage="Don't want to wait to see someone in person? Get pediatric care quickly through Blueberry Pediatrics at no cost to you." />
      </Typography>
      <Tile variant="solid" color="info" sx={{ alignItems: "flex-start" }}>
        <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.md)} alignItems="flex-start">
          <Box
            sx={{
              inlineSize: 122,
              blockSize: 122,
              flexShrink: 0,
              alignSelf: "flex-start",
              borderRadius: theme.tokens.borderRadius.md,
              backgroundColor: "#235AFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Box
              component="img"
              src="/images/blueberry-logo.png"
              alt={intl.formatMessage({ defaultMessage: "Blueberry Pediatrics" })}
              sx={{
                inlineSize: 95,
                blockSize: "auto"
              }}
            />
          </Box>
          <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.sm)}>
            {content.heading && <Typography variant="h6">{content.heading}</Typography>}
            <Typography>{content.bodyContent}</Typography>
            <Box>
              <Button color="secondary" onClick={handleButtonClick}>
                {ctaButtonText}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Tile>
      <AccessBlueberryAccountDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Stack>
  )
}
